import {
  Alert,
  Skeleton,
  SkeletonBlock,
  SkeletonContent,
  SkeletonLine,
  Typography,
} from '@one-thd/sui-atomic-components';
import { AuthorizeCard } from '@one-thd/sui-icons';
import { AxiosError } from 'axios';
import React, { JSX } from 'react';
import { usePurchaseHistoryContext } from '../../../hooks/useContexts';
import ILineItem from '../../../models/line-item';
import { IFulfillmentGroup } from '../../../models/order-details';
import { Layout } from '../../../models/purchase-history';
import { retrieveTrackingNumber } from '../../../utils/general';
import { getReturnStatus } from '../../../utils/order-details/OrderDetailsUtils';
import {
  getOrderType,
  returnDateOrderDetailsText,
  statusStepperData,
} from '../../../utils/order-history/card-utils';
import { TrackingDetails } from '../card-view/index';
import { isMultipleExceptions } from './utils';
import { fulfillmentStatus } from '../util/shared';
import { ProductImages } from '../table-view';
import { ODFulfilmentGroups, ODTableOrder } from './ODInterfaces';
import { FetchDeliveryTrackerInfo } from '../../../hooks/delivery-tracker-hooks';
import { getSvocId } from '../../../utils/user';
import { IDeliveryTrackerRequest } from '../../../models/common';

interface IODExpandedTableRow {
  localDeliveryOrder?: ODTableOrder;
}

const expandedErrorCard = (error: AxiosError) => {
  return (
    <div style={{ width: '880px' }}>
      <Alert status="error" size="large">
        {error.message}
      </Alert>
    </div>
  );
};

export const ODExpandedTableRow = ({
  localDeliveryOrder,
}: IODExpandedTableRow): JSX.Element => {
  const { localDeliveryError, localDeliveryLoaded } =
    usePurchaseHistoryContext();

  const LDOrderFulfillments = localDeliveryOrder?.fulfillmentGroups || [];

  if (localDeliveryError) {
    return expandedErrorCard(localDeliveryError);
  }

  if (localDeliveryLoaded === false) {
    return (
      <div
        className="sui-flex sui-flex-col sui-ml-[16px] sui-p-4"
        style={{ border: '1px solid #CCCCCB', minWidth: '880px' }}
      >
        <Skeleton disablePadding orientation="horizontal">
          <SkeletonContent
            disablePadding
            initialSize="2/4"
            style={{ width: '195px', height: '50px' }}
          >
            <SkeletonLine variant="single" />
            <SkeletonLine variant="single" />
          </SkeletonContent>
          <SkeletonContent
            disablePadding
            initialSize="1/4"
            style={{ width: '50px', height: '50px' }}
          >
            <SkeletonBlock />
          </SkeletonContent>
        </Skeleton>
      </div>
    );
  }

  const orderType = getOrderType(
    localDeliveryOrder?.orderNumber ?? localDeliveryOrder?.orderNumbers?.[0],
  );

  const hasExpections =
    LDOrderFulfillments?.[0]?.bodfsDeliveryAndHandling?.bodfsDeliveries?.[0]
      ?.workOrderException ||
    LDOrderFulfillments?.[0]?.shippingAndHandling?.applianceDeliveries?.[0]
      ?.workOrderException;

  const defaultElement = () =>
    localDeliveryOrder?.fulfillmentGroups?.map(
      (fulfillment: IFulfillmentGroup | ILineItem | null, index: number) => {
        const { isReturnedMultiReturn } = getReturnStatus(
          fulfillment as IFulfillmentGroup,
        );

        const trackingNumber = retrieveTrackingNumber(
          (fulfillment as IFulfillmentGroup)?.shippingAndHandling?.shipments
            ?.shipment ?? [],
        );

        const workOrderNumber =
          (fulfillment as IFulfillmentGroup)?.shippingAndHandling
            ?.applianceDeliveries?.[0]?.workOrderNo ||
          (fulfillment as IFulfillmentGroup)?.bodfsDeliveryAndHandling
            ?.bodfsDeliveries?.[0]?.workOrderNo ||
          undefined;

        const dontShowNewTracker =
          orderType === 'INSTORE' ||
          (fulfillment as IFulfillmentGroup)?.fulfillmentType ===
            'BuyOnlinePickupInStore' ||
          (fulfillment as IFulfillmentGroup)?.fulfillmentType ===
            'unavailable' ||
          (fulfillment as IFulfillmentGroup)?.lineItems?.[0]
            ?.statusDescription === 'Canceled' ||
          (fulfillment as IFulfillmentGroup)?.lineItems?.[0]
            ?.statusDescription === 'Returned';

        const orderNumber =
          localDeliveryOrder?.orderNumber ??
          localDeliveryOrder?.orderNumbers?.[0];
        const requestFulfillmentType: Record<string, string | undefined> = {
          BuyOnlineDeliverFromStore: 'BODFS',
          DFS: 'DFS',
          Appliance: 'MA',
          '': undefined,
        };
        const request: IDeliveryTrackerRequest = {
          deliveryTrackerRequest: {
            customerId: getSvocId(),
            fulfillmentType:
              requestFulfillmentType[
                (fulfillment as IFulfillmentGroup)?.fulfillmentType ?? ''
              ],
            orderNumber: orderNumber ?? null,
            trackingNumber: trackingNumber ?? null,
            workOrderNumber: workOrderNumber ?? null,
          },
        };
        const { deliveryTrackerData, deliveryTrackerLoaded } =
          FetchDeliveryTrackerInfo(request);
        return (
          <div
            className="sui-flex sui-w-full"
            style={{
              minHeight: '50px',
            }}
            key={index}
          >
            {/* <PHDeliveryTracker
              deliveryData={testData as unknown as IDeliveryAPI}
              fulfillmentGroup={fulfillment as IFulfillmentGroup}
              deliveryDataLoaded={orderDetailsLoaded}
              preShip={false}
              variant="table"
            /> */}
            {dontShowNewTracker ? (
              <div className="sui-flex sui-items-center sui-w-full">
                <div
                  style={{
                    overflow: 'hidden',
                  }}
                  className="sui-flex sui-w-[39%]"
                >
                  {orderType && (
                    <div className="sui-flex-col">
                      {(fulfillment as IFulfillmentGroup)?.fulfillmentType !==
                      'unavailable' ? (
                        <div className="sui-pl-[20px]">
                          <Typography variant="body-base" weight="bold">
                            {statusStepperData(
                              fulfillment,
                              ['InStorePurchase', 'InStore'].includes(
                                (fulfillment as IFulfillmentGroup)
                                  ?.fulfillmentType ?? '',
                              )
                                ? 'expandedTitle'
                                : 'title',
                              (fulfillment as IFulfillmentGroup)
                                ?.fulfillmentType,
                              orderType === 'INSTORE',
                            )}
                          </Typography>
                        </div>
                      ) : null}
                      <div className="sui-pl-[20px]">
                        {fulfillmentStatus(fulfillment, 'table')}
                      </div>
                    </div>
                  )}
                </div>
                <div className="sui-w-[33%]">
                  <ProductImages
                    isLoading={!localDeliveryLoaded}
                    lineItems={
                      (fulfillment as IFulfillmentGroup)?.lineItems ?? []
                    }
                    maxShow={5}
                    variant="medium"
                  />
                </div>
                <div className="sui-w-[25%]">
                  {isReturnedMultiReturn ? (
                    <Typography variant="body-base" weight="bold">
                      {returnDateOrderDetailsText(
                        fulfillment as IFulfillmentGroup,
                      )}
                    </Typography>
                  ) : (
                    <TrackingDetails
                      isMobile={false}
                      isLoading={!localDeliveryLoaded}
                      item={fulfillment as IFulfillmentGroup}
                      orderNumber={
                        (localDeliveryOrder?.orderNumber ??
                          localDeliveryOrder?.orderNumbers?.[0]) ||
                        ''
                      }
                      record={localDeliveryOrder}
                      trackingNumber={trackingNumber}
                      workOrderNumber={workOrderNumber}
                      layout={'TABLE' as Layout}
                    />
                  )}
                </div>
              </div>
            ) : (
              <TrackingDetails
                isMobile={false}
                isLoading={!localDeliveryLoaded}
                item={fulfillment as IFulfillmentGroup}
                orderNumber={
                  (localDeliveryOrder?.orderNumber ??
                    localDeliveryOrder?.orderNumbers?.[0]) ||
                  ''
                }
                record={localDeliveryOrder}
                trackingNumber={trackingNumber}
                workOrderNumber={workOrderNumber}
                layout={'TABLE' as Layout}
                view="LOCAL_DELIVERIES"
                deliveryTrackerData={deliveryTrackerData}
                deliveryTrackerLoaded={deliveryTrackerLoaded}
              />
            )}
          </div>
        );
      },
    );

  const expectionsElement = () =>
    isMultipleExceptions(LDOrderFulfillments) === 'Multiple' ? (
      <>
        {LDOrderFulfillments?.map((LDOrderFulfillment: ODFulfilmentGroups) => {
          return (
            <div
              className="sui-flex sui-w-full"
              style={{
                minHeight: '50px',
              }}
            >
              <div className="sui-flex sui-items-center sui-w-full">
                <div
                  style={{
                    overflow: 'hidden',
                  }}
                  className="sui-flex sui-w-[70%]"
                >
                  {LDOrderFulfillment?.shippingAndHandling
                    ?.applianceDeliveries?.[0]?.workOrderException ===
                    'Delivery Attempted' ||
                  LDOrderFulfillment?.bodfsDeliveryAndHandling
                    ?.bodfsDeliveries?.[0]?.workOrderException ===
                    'Delivery Attempted' ? (
                    <Alert status="warning" size="large">
                      Looks like we missed you. Please call 800-466-3337 to
                      reschedule your delivery.
                    </Alert>
                  ) : (
                    <Typography variant="body-base" weight="bold">
                      No Delivery Date Scheduled
                    </Typography>
                  )}
                  <div className="sui-w-[33%] sui-pl-28">
                    <ProductImages
                      isLoading={!localDeliveryLoaded}
                      lineItems={LDOrderFulfillment?.lineItems ?? []}
                      maxShow={5}
                      variant="medium"
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </>
    ) : (
      <>
        <div
          className="sui-flex sui-w-full"
          style={{
            minHeight: '50px',
          }}
        >
          <div className="sui-flex sui-items-center sui-w-full">
            <div
              style={{
                overflow: 'hidden',
              }}
              className="sui-flex sui-w-[70%]"
            >
              {LDOrderFulfillments?.[0]?.shippingAndHandling
                ?.applianceDeliveries?.[0]?.workOrderException ===
                'Delivery Attempted' ||
              LDOrderFulfillments?.[0]?.bodfsDeliveryAndHandling
                ?.bodfsDeliveries?.[0]?.workOrderException ===
                'Delivery Attempted' ? (
                <Alert status="warning" size="large">
                  Looks like we missed you. Please call 800-466-3337 to
                  reschedule your delivery.
                </Alert>
              ) : (
                <div className="sui-flex sui-flex-col sui-gap-3">
                  <Typography variant="body-base" weight="bold">
                    No Delivery Date Scheduled
                  </Typography>
                  {LDOrderFulfillments?.[0]?.lineItems?.[0]
                    ?.statusDescription === 'Being Processed' && (
                    <div className="sui-flex sui-items-center sui-gap-[8px]">
                      <AuthorizeCard size="small" />
                      <Typography variant="body-base">Processed</Typography>
                    </div>
                  )}
                </div>
              )}
              <div className="sui-w-[33%] sui-pl-28">
                <ProductImages
                  isLoading={!localDeliveryLoaded}
                  lineItems={LDOrderFulfillments?.[0]?.lineItems ?? []}
                  maxShow={5}
                  variant="medium"
                />
              </div>
            </div>
          </div>
        </div>
      </>
    );

  return (
    <>
      <div
        className="sui-flex sui-gap-[16px] sui-flex-col sui-ml-[16px] sui-p-4"
        style={{ border: '1px solid #CCCCCB', minWidth: '880px' }}
      >
        {hasExpections ? expectionsElement() : defaultElement()}
      </div>
    </>
  );
};
